import { useFormatters } from '@/hooks/useFormatters';
import { useGlobalModal } from '@/hooks/useGlobalModal';
import { useDispatch } from '@/store';
import { useSuspenseQuery } from '@apollo/client';
import GlobalModal from '@components/GlobalModal';
import ModalContent from '@components/modal/ModalContent';
import TruncatedTypography from '@components/TruncatedTypography';
import { Access } from '@features/authorization';
import { openDeviceProfileModal } from '@features/deviceProfile/slices/deviceProfileSlice';
import { setModalDisplayedStationChart } from '@features/devicesAnalysis/slices/devicesAnalysisSlice';
import StandardAutocompleteIdsWithFallback from '@features/standardDesign/components/autocomplete/StandardAutocompleteIds';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Article, QueryStats, Unarchive } from '@mui/icons-material';
import { Autocomplete, Box, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import Fuse from 'fuse.js';
import { useMemo, useState, type FC } from 'react';
import { List, RowComponentProps } from 'react-window';
import GET_DEVICES_GRAVEYARD, { GraveyardDevice } from '../graphql/queries/getDevicesGraveyard';
import { RestoreDeviceModal } from './RestoreDeviceModal';

const ORDER_BY_OPTIONS = ['id', 'name', 'deletedAt'] as const;

const SuspenseComponent: FC = () => {
  const [filters, setFilters] = useState({
    search: '',
    organizationId: null as number | null,
  });
  const [orderBy, setOrderBy] = useState<(typeof ORDER_BY_OPTIONS)[number]>('deletedAt');
  const [restoreDeviceId, setRestoreDeviceId] = useState<string | null>(null);
  const { data } = useSuspenseQuery(GET_DEVICES_GRAVEYARD);
  const dispatch = useDispatch();
  const { i18n } = useLingui();

  const openDeviceProfile = (deviceId: string) => {
    dispatch(
      openDeviceProfileModal({
        deviceId,
      }),
    );
  };

  const openDevicesAnalysisModal = (deviceId: string) => {
    dispatch(setModalDisplayedStationChart(deviceId));
  };

  const openRestoreDeviceModal = (deviceId: string) => {
    setRestoreDeviceId(deviceId);
  };

  const onCloseRestoreDevice = () => [setRestoreDeviceId(null)];

  const filteredDevices = useMemo(() => {
    let filteredItems = [...data.devices];

    if (orderBy === 'deletedAt') {
      filteredItems.sort((a, b) => {
        const lastA = a.profile.operationalStates.at(-1);
        const deletedA = lastA ? lastA.timestamp : 0;
        const lastB = b.profile.operationalStates.at(-1);
        const deletedB = lastB ? lastB.timestamp : 0;
        return deletedB - deletedA;
      });
    }

    if (orderBy === 'name') {
      filteredItems.sort((a, b) => {
        const nameA = a.profile.name;
        const nameB = b.profile.name;
        return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
      });
    }

    if (filters.organizationId != null) {
      filteredItems = filteredItems.filter(
        (device) => device.profile.organization?.id === filters.organizationId,
      );
    }

    if (filters.search) {
      const fuse = new Fuse(filteredItems, {
        keys: ['id', 'profile.name'],
      });
      filteredItems = fuse.search(filters.search).map(({ item }) => item);
    }

    return filteredItems;
  }, [filters, data.devices, orderBy]);

  return (
    <Box>
      <RestoreDeviceModal
        open={restoreDeviceId !== null}
        onClose={onCloseRestoreDevice}
        deviceId={restoreDeviceId}
      />
      <Box mb={1} display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={1}>
        <TextField
          value={filters.search}
          onChange={(event) =>
            setFilters((filters) => ({ ...filters, search: event.target.value }))
          }
          label="Buscar"
          size="small"
        />
        <StandardAutocompleteIdsWithFallback
          value={filters.organizationId}
          onChange={(_, organizationId) =>
            setFilters((filters) => ({ ...filters, organizationId }))
          }
          size="small"
          dataType="organizations"
        />
        <Autocomplete
          value={orderBy}
          disableClearable
          onChange={(_, value) => setOrderBy(value)}
          options={ORDER_BY_OPTIONS}
          getOptionLabel={(option) => {
            if (option === 'id') return 'ID';
            if (option === 'name') return t(i18n)`Nombre`;
            if (option === 'deletedAt') return t(i18n)`Fecha de archivado`;
            return option;
          }}
          size="small"
          renderInput={(params) => <TextField {...params} label="Ordenar" />}
        />
      </Box>
      <Box border={1} borderColor="divider" borderRadius={1} height={550}>
        <List
          rowCount={filteredDevices.length}
          rowHeight={42}
          rowComponent={RowComponent}
          rowProps={{
            devices: filteredDevices,
            openDevicesAnalysisModal,
            openDeviceProfileModal: openDeviceProfile,
            openRestoreDeviceModal,
          }}
        />
      </Box>
    </Box>
  );
};

const RowComponent: FC<
  RowComponentProps<{
    devices: GraveyardDevice[];
    openDevicesAnalysisModal: (deviceId: string) => void;
    openDeviceProfileModal: (deviceId: string) => void;
    openRestoreDeviceModal: (deviceId: string) => void;
  }>
> = ({
  index,
  style,
  devices,
  openDeviceProfileModal,
  openDevicesAnalysisModal,
  openRestoreDeviceModal,
}) => {
  const { i18n } = useLingui();
  const { formatMetricValue, formatRelativeTime, formatDateTime } = useFormatters();

  const device = devices[index];
  const { flow, level, velocity } = device.metrics;
  const lastOperationalState = device.profile.operationalStates.at(-1);
  const deletedAt = lastOperationalState ? lastOperationalState.timestamp : null;

  const getDescription = (metric: typeof flow, label: string) => {
    const { lastData, unit, precision } = metric;
    return lastData ? (
      <Typography fontSize="inherit">
        <b>{label}:</b>{' '}
        {formatMetricValue(lastData[1], {
          precision,
          unit,
        })}{' '}
        {formatRelativeTime(lastData[0])} <i>({formatDateTime(lastData[0])})</i>
      </Typography>
    ) : null;
  };
  const lastFlowDescription = getDescription(flow, 'Caudal');
  const lastLevelDescription = getDescription(level, 'Nivel');
  const lastVelocityDescription = getDescription(velocity, 'Velocidad');
  return (
    <Box
      style={style}
      key={device.id}
      display="grid"
      gridTemplateColumns="120px minmax(0, 1fr) auto"
      gap={2}
      borderBottom={index < devices.length - 1 ? 1 : 0}
      borderColor="divider"
      px={1}
      py="2px"
      alignItems="center"
    >
      <Box borderRight={1} fontSize="small" borderColor="divider">
        {deletedAt ? (
          <Typography fontWeight="bold" color="error.light" fontSize="inherit">
            {formatDateTime(deletedAt, {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Typography>
        ) : (
          <Typography fontWeight="bold" color="error.light" fontSize="inherit">
            Sin información
          </Typography>
        )}
      </Box>

      <Box>
        <Box display="flex" gap={2}>
          <TruncatedTypography fontSize="14px">{device.profile.name}</TruncatedTypography>
          <TruncatedTypography fontSize="14px" color="text.secondary">
            {device.profile.organization?.name}
          </TruncatedTypography>
        </Box>
        <TruncatedTypography fontSize="11px" color="text.secondary">
          {device.id}
        </TruncatedTypography>
      </Box>
      <Box display="flex" gap={1}>
        <Tooltip title={t(i18n)`Abrir perfil`}>
          <IconButton size="small" onClick={() => openDeviceProfileModal(device.id)}>
            <Article fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={
            <Box>
              <Typography fontSize="inherit">
                <Trans>Últimos datos</Trans>
              </Typography>
              {!lastFlowDescription && !lastLevelDescription && !lastVelocityDescription && (
                <Typography fontSize="inherit">Sin datos de caudal, nivel ni velocidad</Typography>
              )}
              {lastFlowDescription}
              {lastLevelDescription}
              {lastVelocityDescription}
            </Box>
          }
        >
          <IconButton size="small" onClick={() => openDevicesAnalysisModal(device.id)}>
            <QueryStats fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t(i18n)`Desarchivar`}>
          <span>
            <IconButton size="small" onClick={() => openRestoreDeviceModal(device.id)}>
              <Unarchive fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

export const DevicesGraveyard: FC = () => {
  const { globalModalProps, onClose } = useGlobalModal('devicesGraveyardModal');

  return (
    <GlobalModal
      {...globalModalProps}
      onClose={onClose}
      dialogProps={{ maxWidth: 'lg' }}
      authorization={{
        access: Access.ViewDeletedDevices,
        onClose,
      }}
      modalTitleProps={{
        title: <Trans>Cementerio de dispositivos</Trans>,
        onClose,
      }}
      // modalActionProps={{
      //   onClose,
      // }}
    >
      <ModalContent topDivider>
        <SuspenseComponent />
      </ModalContent>
    </GlobalModal>
  );
};
